import QueryString from 'qs';

import type {
  Affiliation,
  AffiliationWithRules,
  Agency,
  Agent,
  AgentHubBooking,
  AgentHubInvoiceDetails,
  AvailableAffiliations,
  Commission,
  CreateAffiliationParams,
  CreateCommissionParams,
  CreateCommissionResponse,
  CreateDiscountVoucherParams,
  CreateDiscountVoucherResponse,
  CreateEoiParams,
  CreateRegistrationTokenParams,
  EditAgencyParams,
  EditAgencyResponse,
  EditCommissionParams,
  EditUserParams,
  Eoi,
  GetAffiliationResponse,
  GetAgenciesByIdResponse,
  GetAgenciesResponse,
  GetAgentsResponse,
  GetAvailableAffiliationsResponse,
  List,
  ListAffiliationsParams,
  ListAffiliationsResponse,
  ListAgencyParams,
  ListEoiParams,
  ListQuotedCommissionsParams,
  ListQuotedCommissionsResponse,
  ListUserEmailLogsParams,
  ListUserParams,
  MappedAgent,
  MoveUserAgencyParams,
  PreviewMoveUserAgencyParams,
  PreviewMoveUserAgencyResponse,
  UpdateAffiliationParams,
  UpdateAffiliationResponse,
  UserEmailLogsResponse,
  getCommissionsParams,
  getCommissionsResponse,
} from '~/types/services/agentHub';

import { formatDateWithClock, isBefore } from '../TimeService';
import { request } from '../common';

export function basePath() {
  return window.configs.API_HOST + '/api/agent';
}

export async function listEOI(params: ListEoiParams): Promise<List<Eoi>> {
  const { result } = await request(`${basePath()}/eoi?${QueryString.stringify(params)}`, { method: 'GET' });

  return {
    total: result.total,
    rows: result.rows.map((eoi) => {
      return {
        id: eoi.id,
        agencyName: eoi.agencyName,
        givenName: eoi.givenName,
        surname: eoi.surname,
        status: eoi.status,
        submittedAt: formatDateWithClock(eoi.submittedAt),
        updatedAt: getUpdatedAtDate(eoi),
        regionCode: eoi.regionCode,
        stateOrTerritory: eoi.stateOrTerritory,
        onboardedAt: eoi.onboardedAt ? formatDateWithClock(eoi.onboardedAt) : undefined,
        customerId: eoi?.customerId,
        userStatus: eoi.userStatus,
      };
    }),
  };
}

export async function getEOI(id: string): Promise<Eoi> {
  const { result } = await request(`${basePath()}/eoi/${id}`, { method: 'GET' });
  return result;
}

export async function deleteEOI(id: string): Promise<void> {
  await request(`${basePath()}/eoi/${id}`, { method: 'DELETE' });
}

export async function updateEOIStatus(id: string, status: 'approved' | 'rejected'): Promise<Eoi> {
  const requestBody = {
    status,
  };

  const { result } = await request(`${basePath()}/eoi/${id}`, {
    headers: {
      'Content-Type': 'application/json',
    },
    method: 'PATCH',
    body: JSON.stringify(requestBody),
  });
  return result;
}

export async function updateEOIDetails(id: string, requestBody: Eoi): Promise<Eoi> {
  const { result } = await request(`${basePath()}/eoi/${id}`, {
    headers: {
      'Content-Type': 'application/json',
    },
    method: 'PUT',
    body: JSON.stringify(requestBody),
  });
  return result;
}

export async function listAffiliations(params: ListAffiliationsParams): Promise<List<AffiliationWithRules>> {
  const { result }: ListAffiliationsResponse = await request(
    `${basePath()}/affiliation?${QueryString.stringify(params)}`,
    { method: 'GET' },
  );
  return result;
}

export async function listAgencies(params: ListAgencyParams): Promise<List<Agency>> {
  const { result }: GetAgenciesResponse = await request(`${basePath()}/agency?${QueryString.stringify(params)}`, {
    method: 'GET',
  });

  const agenciesResult: List<Agency> = {
    total: result.total,
    rows: result.rows.map((agency) => ({
      id: agency.id,
      name: agency.name,
      businessCodeType: agency.businessCodeType,
      businessCodeValue: agency.businessCodeValue,
      address: agency.address,
      city: agency.city,
      state: agency.state,
      postcode: agency.postcode,
      country: agency.country,
      affiliation: agency.affiliation,
      tier: agency?.tier,
      isAgentRecordsEnabled: agency.isAgentRecordsEnabled,
    })),
  };

  return agenciesResult;
}

export async function listUsers(params: ListUserParams): Promise<List<MappedAgent>> {
  const { result }: GetAgentsResponse = await request(`${basePath()}/user?${QueryString.stringify(params)}`, {
    method: 'GET',
  });

  const agentsResult: List<MappedAgent> = {
    total: result.total,
    rows: result.rows.map((agent) => ({
      id: agent.id,
      name: agent.name ?? '',
      phone: agent.phone ?? '',
      email: agent.email ?? '',
      affiliation: agent.affiliation,
      customerId: agent.customerId ?? '',
      agencyId: agent.agencyId ?? '',
      eoiSubmissionId: agent.eoiSubmissionId ?? '',
      agencyName: agent.agencyName ?? '',
      status: agent.status,
      verifiedAt: agent.verifiedAt,
      ...(agent.isAgencyAdmin !== undefined ? { isAgencyAdmin: agent.isAgencyAdmin } : {}),
      ...(agent.isAffiliationAdmin !== undefined ? { isAffiliationAdmin: agent.isAffiliationAdmin } : {}),
    })),
  };

  return agentsResult;
}

export async function updateUser(userId: string, params: EditUserParams): Promise<Agent> {
  const { result } = await request(`${basePath()}/user/${userId}`, {
    method: 'PUT',
    body: JSON.stringify(params),
    headers: {
      'Content-Type': 'application/json',
    },
  });
  return result;
}

export async function previewMoveUserAgency(
  userId: string,
  params: PreviewMoveUserAgencyParams,
): Promise<PreviewMoveUserAgencyResponse> {
  const { result } = await request(`${basePath()}/user/${userId}/agency/move-preview`, {
    method: 'POST',
    body: JSON.stringify(params),
    headers: {
      'Content-Type': 'application/json',
    },
  });
  return result;
}

export async function moveUserAgency(userId: string, params: MoveUserAgencyParams): Promise<Agent> {
  const { result } = await request(`${basePath()}/user/${userId}/agency`, {
    method: 'PATCH',
    body: JSON.stringify(params),
    headers: {
      'Content-Type': 'application/json',
    },
  });
  return result;
}

export async function fetchAgentHubUserById(id: string): Promise<Agent> {
  const { result } = await request(`${basePath()}/user/${id}`, {
    method: 'GET',
  });
  return result;
}

export async function getAgency(id: string, brand: App.Brands): Promise<Agency> {
  const qs = QueryString.stringify({ brand });
  const { result } = await request(`${basePath()}/agency/${id}?${qs}`, { method: 'GET' });
  return result;
}

export async function getBookingInvoiceDetails(orderId: string, brand: App.Brands): Promise<AgentHubInvoiceDetails> {
  const qs = QueryString.stringify({ brand });
  const { result } = await request(`${basePath()}/bookings/${orderId}/invoice-details?${qs}`, { method: 'GET' });
  return result;
}

export async function getAffiliation(id: string): Promise<AffiliationWithRules> {
  const { result }: GetAffiliationResponse = await request(`${basePath()}/affiliation/${id}`, { method: 'GET' });
  return result;
}

interface GetAffiliationListOptions {
  includeWorld?: boolean;
}

export async function getAffiliationList(
  regionCode?: string,
  options: GetAffiliationListOptions = {},
): Promise<AvailableAffiliations> {
  const { includeWorld = true } = options;
  const region = includeWorld ? [regionCode || 'AU', 'world'] : [regionCode || 'AU'];
  const qs = QueryString.stringify({ region }, { arrayFormat: 'repeat' });
  const { result }: GetAvailableAffiliationsResponse = await request(`${basePath()}/available-affiliations?${qs}`, {
    method: 'GET',
  });

  return result;
}

export async function createAffiliation(data: CreateAffiliationParams): Promise<Affiliation> {
  const { result }: GetAffiliationResponse = await request(`${basePath()}/affiliation`, {
    method: 'POST',
    body: JSON.stringify(data),
    headers: {
      'Content-Type': 'application/json',
    },
  });
  return result;
}

export async function deleteAffiliation(id: string): Promise<void> {
  await request(`${basePath()}/affiliation/${id}`, { method: 'DELETE' });
}

export async function updateAffiliation(id: string, data: UpdateAffiliationParams): Promise<Affiliation> {
  const { result }: UpdateAffiliationResponse = await request(`${basePath()}/affiliation/${id}`, {
    method: 'PUT',
    body: JSON.stringify(data),
    headers: {
      'Content-Type': 'application/json',
    },
  });
  return result;
}

export async function updateAgency(id: string, data: EditAgencyParams): Promise<EditAgencyResponse> {
  const { result } = await request(`${basePath()}/agency/${id}`, {
    method: 'PUT',
    body: JSON.stringify(data),
    headers: {
      'Content-Type': 'application/json',
    },
  });
  return result;
}

export async function listUserEmailLogs(id: string, params: ListUserEmailLogsParams): Promise<UserEmailLogsResponse> {
  const { result } = await request(`${basePath()}/user/${id}/email-logs?${QueryString.stringify(params)}`, {
    method: 'GET',
  });
  return result;
}

export async function resendActivationEmail(id: string): Promise<boolean> {
  const { result } = await request(`${basePath()}/user/${id}/resend-activation-email`, {
    method: 'POST',
  });
  return result;
}

export async function listRegistrationTokens(id: string, params) {
  const { result } = await request(`${basePath()}/user/${id}/registration-tokens?${QueryString.stringify(params)}`, {
    method: 'GET',
  });
  return {
    total: result.total,
    rows: result.rows.map((token) => ({
      id: token.id,
      createdAt: formatDateWithClock(token.createdAt),
      expiresAt: formatDateWithClock(token.expiresAt),
      expired: isBefore(token.expiresAt),
      isUsed: token.isUsed,
    })),
  };
}

export async function createRegistrationToken(id: string, data: CreateRegistrationTokenParams) {
  const { result } = await request(`${basePath()}/user/${id}/registration-token`, {
    method: 'POST',
    body: JSON.stringify(data),
    headers: {
      'Content-Type': 'application/json',
    },
  });
  return result;
}

export async function getUser(id: string): Promise<Agent> {
  const { result } = await request(`${basePath()}/user/${id}`, {
    method: 'GET',
  });
  return result;
}

export async function createCommission(params: CreateCommissionParams): Promise<CreateCommissionResponse> {
  const { result } = await request(`${basePath()}/commission`, {
    method: 'POST',
    body: JSON.stringify(params),
    headers: {
      'Content-Type': 'application/json',
    },
  });
  return result;
}

export async function getCommissions(params: getCommissionsParams): Promise<List<Commission>> {
  const res: getCommissionsResponse = await request(`${basePath()}/commission?${QueryString.stringify(params)}`, {
    method: 'GET',
  });

  const rows: Array<Commission> = res.result.rows.map((rule) => {
    const ruleWithExtras = rule as Commission;
    return {
      id: rule.id,
      description: rule.description,
      commissionPercentage: rule.commissionPercentage,
      commissionValueLimits: rule.commissionValueLimits ?? [],
      commissionType: rule.type,
      startDate: rule.startDate,
      endDate: rule.endDate,
      type: rule.type,
      regions: rule.regions,
      createdAt: rule.createdAt,
      updatedAt: rule.updatedAt,
      createdBy: rule.createdBy,
      updatedBy: rule.updatedBy,
      limit: rule.limit,
      hasReachedLimit: rule.hasReachedLimit,
      priority: rule.priority,
      canHaveAdditionalCommissions: rule.canHaveAdditionalCommissions,
      commissionGroups: ruleWithExtras.commissionGroups ?? [],
      includesTaxesAndFees: ruleWithExtras.includesTaxesAndFees,
      includesMargin: ruleWithExtras.includesMargin,
    };
  });

  return { ...res.result, rows };
}

export async function listQuotedCommissions(
  params: ListQuotedCommissionsParams,
): Promise<ListQuotedCommissionsResponse> {
  const { result } = await request(`${basePath()}/quoted-commission?${QueryString.stringify(params)}`, {
    method: 'GET',
  });

  return result;
}

export async function updateCommission(commissionId: string, data: EditCommissionParams) {
  await request(`${basePath()}/commission/${commissionId}`, {
    method: 'PUT',
    body: JSON.stringify(data),
    headers: {
      'Content-Type': 'application/json',
    },
  });
}
export async function deleteCommission(id: string) {
  await request(`${basePath()}/commission/${id}`, { method: 'DELETE' });
}

function getUpdatedAtDate(eoi: Eoi) {
  if (eoi.approvedAt) {
    return formatDateWithClock(eoi.approvedAt);
  }
  if (eoi.rejectedAt) {
    return formatDateWithClock(eoi.rejectedAt);
  }

  return undefined;
}

export async function getAgenciesById(id: Array<string>) {
  const query = QueryString.stringify({ id });
  const { result }: GetAgenciesByIdResponse = await request(`${basePath()}/agency/list-by-ids?${query}`, {
    method: 'GET',
  });

  const agencies = result.rows.map((agency) => {
    return {
      id: agency.id,
      name: agency.name,
      businessCodeType: agency.businessCodeType,
      businessCodeValue: agency.businessCodeValue,
      address: agency.address,
      city: agency.city,
      state: agency.state,
      postcode: agency.postcode,
      country: agency.country,
      affiliation: agency.affiliation,
    };
  });

  return agencies;
}

export async function updateAgencyUsersTier(agencyId: string, tier: string) {
  await request(`${basePath()}/agency/${agencyId}/users/tier`, {
    method: 'PUT',
    body: JSON.stringify({ tier }),
    headers: {
      'Content-Type': 'application/json',
    },
  });
}

export async function updateAffiliationUsersTier(affiliation: string, affiliationRegion: string, tier: string) {
  await request(`${basePath()}/affiliation/users/tier`, {
    method: 'PUT',
    body: JSON.stringify({ affiliation, affiliationRegion, tier }),
    headers: {
      'Content-Type': 'application/json',
    },
  });
}

export async function uploadCommissionPayment(formData: FormData) {
  await request(`${basePath()}/finance/upload-commissions-payment`, {
    method: 'POST',
    body: formData,
  });
}

export async function getAgentHubBookingByOrderId(orderId: string): Promise<AgentHubBooking> {
  const { result } = await request(`${basePath()}/bookings/${orderId}`, {
    method: 'GET',
  });
  return result;
}

export async function offboardUser(userId: string): Promise<void> {
  await request(`${basePath()}/user/${userId}/offboarded`, {
    method: 'POST',
  });
}

export async function onboardUser(userId: string): Promise<void> {
  await request(`${basePath()}/user/${userId}/onboarded`, {
    method: 'POST',
  });
}

export async function createDiscountVoucher(
  params: CreateDiscountVoucherParams,
): Promise<CreateDiscountVoucherResponse['result']> {
  const { result } = await request(`${basePath()}/discount-vouchers`, {
    method: 'POST',
    body: JSON.stringify(params),
    headers: {
      'Content-Type': 'application/json',
    },
  });
  return result;
}

export async function createEOI(params: CreateEoiParams): Promise<Eoi> {
  const { result } = await request(`${basePath()}/eoi`, {
    method: 'POST',
    body: JSON.stringify(params),
    headers: {
      'Content-Type': 'application/json',
    },
  });
  return result;
}
